import { captureException } from '@/utils/sentry';
import { getOfflineModeActive } from '@/utils/offline-mode';
import {
  gql,
  MutationResult,
  MutationTuple,
  useMutation,
} from '@apollo/client';
import { GTINMetadata } from '@/contexts/item-context-types';
import { GtinMetadataFormatType } from '@/enums';
import { GtinSettings } from '@/utils/gtin-metadata';
import {
  ItemsContext_MultiOrderGuideItemsQuery,
  NewOrder_OrderContextQuery,
  NewOrder_OrderContextQueryVariables,
  NewOrder_OrderFieldsFragment,
  NewOrder_OrderItemFieldsFragment,
  NewOrder_VisibleVariantPackQuery,
  OrderDomain,
  OrderSummary_ItemListFieldsFragment,
  OrderSummary_ValidateOrderMutation,
  OrderSummary_ValidateOrderMutationVariables,
} from '@/types';
import {
  OfflineMinimalVariantPackAdapter,
  OfflineOrderContextAdapter,
  OfflineOrderGuideAdapter,
  OfflineVisibleVariantPackAdapter,
  VariantPackEntry,
} from '@/adapters/offline-mode';
import {
  ORDER_CONTEXT_QUERY,
  useOrderContextQueryVariables,
} from '@/contexts/new-order-context-queries';
import { ORDER_FEES_FIELDS_FRAGMENT } from '@/utils/money';
import {
  ORDER_SUMMARY_ITEM_LIST_FIELDS_FRAGMENT,
  ORDER_SUMMARY_PROMOTION_FIELDS_FRAGMENT,
} from '@/components/orders/order-summary-list';
import { useCallback, useState } from 'react';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

type ValidateOrderMutationTuple = [
  MutationTuple<
    OrderSummary_ValidateOrderMutation,
    OrderSummary_ValidateOrderMutationVariables
  >[0],
  Pick<MutationResult<OrderSummary_ValidateOrderMutation>, 'loading'>,
];

export const VALIDATE_ORDER_MUTATION = gql`
  fragment ValidateOrderLoyaltyFields on ValidateOrderLoyalty {
    can_enroll
    enrollment_status
    estimated_points
    items {
      estimated_points
      order_item_id
    }
    program_id
    program_name
    supplier_loyalty_program_restaurant_id
  }

  fragment ValidateOrderFields on orders {
    discount_codes
    fulfillment_timeslot_end
    fulfillment_timeslot_start
    fulfillment_type
    orders_items(order_by: $ordersItemsOrderBy) {
      ...OrderSummary_ItemListFields
    }
    order_promotion_rels {
      discount_in_micros
      promotion {
        ...OrderSummary_PromotionFields
      }
      uuid
    }
    restaurant_desired_delivery_time
    status
    supplier {
      supplier_metadata {
        lead_time_split_behavior
        uuid
      }
      uuid
    }
    uuid
  }

  mutation OrderSummary_ValidateOrder(
    $locale: String!
    $orderUUID: uuid!
    $restaurantUUID: uuid!
    $skipSaltApi: Boolean!
    $ordersItemsOrderBy: [orders_items_order_by!]!
    $smartOrderUUID: uuid
  ) {
    validateOrder(order_id: $orderUUID, smart_order_id: $smartOrderUUID) {
      alerts {
        alert_code
        alert_level
        alert_message
        order_fulfillment_date
      }
      can_place_order
      loyalty {
        ...ValidateOrderLoyaltyFields
      }
      missing_essential_item_ids
      order {
        ...ValidateOrderFields
      }
      order_fees {
        amount_at_invoice_micros
        amount_at_order_micros
        display_type
        name
      }
      order_minimum {
        amount
        is_hard
        unit
      }
      section_headers {
        header_text
        order_fulfillment_date
      }
      translated_fulfillment_display_name
    }
  }

  ${ORDER_FEES_FIELDS_FRAGMENT}
  ${ORDER_SUMMARY_ITEM_LIST_FIELDS_FRAGMENT}
  ${ORDER_SUMMARY_PROMOTION_FIELDS_FRAGMENT}
`;

const mapValidateOrderItemToNewOrderItem = (
  validateOrderItem: OrderSummary_ItemListFieldsFragment,
): NewOrder_OrderItemFieldsFragment => {
  return {
    __typename: 'orders_items',
    earliest_fulfillment_at: validateOrderItem.earliest_fulfillment_at ?? null,
    notes: validateOrderItem.notes ?? null,
    order_item_prices: {
      __typename: 'order_item_prices',
      pack_quantity_at_order:
        validateOrderItem.order_item_prices?.pack_quantity_at_order,
    },
    restaurant_display_name: validateOrderItem.restaurant_display_name,
    uuid: validateOrderItem.uuid,
    variants_pack: {
      __typename: 'variants_packs',
      item: {
        __typename: 'items',
        description: validateOrderItem.variants_pack.item.description,
        uuid: validateOrderItem.variants_pack.item.uuid,
      },
      metadata: validateOrderItem.variants_pack.metadata,
      uuid: validateOrderItem.variants_pack.uuid,
    },
  };
};

const useOnlineValidateOrder = ({
  orderDomains,
  restaurantUUID,
  supplierUUID,
}: {
  orderDomains: OrderDomain[];
  restaurantUUID: string;
  supplierUUID: string;
}) => {
  const orderContextQueryVariables = useOrderContextQueryVariables(
    orderDomains,
    restaurantUUID,
    supplierUUID,
  );

  return useMutation<
    OrderSummary_ValidateOrderMutation,
    OrderSummary_ValidateOrderMutationVariables
  >(VALIDATE_ORDER_MUTATION, {
    update: (
      client,
      {
        data: validateOrderData,
      }: { data?: OrderSummary_ValidateOrderMutation | null },
    ) => {
      try {
        const cachedOrderContext = client.readQuery<
          NewOrder_OrderContextQuery,
          NewOrder_OrderContextQueryVariables
        >({
          query: ORDER_CONTEXT_QUERY,
          variables: orderContextQueryVariables,
        });

        // Find index of draft order in the cache that matches the validated order
        const draftOrderIndex =
          cachedOrderContext?.getOrders.findIndex(
            (o) =>
              o.order?.uuid === validateOrderData?.validateOrder.order?.uuid,
          ) ?? -1;
        if (draftOrderIndex === -1) {
          return;
        }

        // Create map of validated order item to their updated quantities
        const validatedOrderItemUUIDToQuantityMap =
          validateOrderData!.validateOrder.order!.orders_items.reduce<{
            [orderItemUUID: string]: number;
          }>((result, oi) => {
            const orderPrices = oi.order_item_prices;
            if (typeof orderPrices?.pack_quantity_at_order === 'number') {
              result[oi.uuid] = orderPrices.pack_quantity_at_order;
            }
            return result;
          }, {});

        const cachedOrders = cachedOrderContext!.getOrders;
        const cachedDraftOrder = cachedOrders[draftOrderIndex];

        // Update quantities for validated order items, otherwise
        // remove cached order items that are no longer present
        const updatedNewOrderContextOrderItems = (
          validateOrderData?.validateOrder.order?.orders_items || []
        ).reduce<NewOrder_OrderItemFieldsFragment[]>((acc, oi) => {
          const validatedOrderItemQuantity =
            validatedOrderItemUUIDToQuantityMap[oi.uuid];
          if (typeof validatedOrderItemQuantity === 'number') {
            acc.push(mapValidateOrderItemToNewOrderItem(oi));
          }
          return acc;
        }, []);

        client.writeQuery<
          NewOrder_OrderContextQuery,
          NewOrder_OrderContextQueryVariables
        >({
          data: {
            ...cachedOrderContext!,
            getOrders: [
              ...cachedOrders.slice(0, draftOrderIndex),
              {
                ...cachedDraftOrder,
                order: {
                  ...cachedDraftOrder.order!,
                  orders_items: updatedNewOrderContextOrderItems,
                },
              },
              ...cachedOrders.slice(draftOrderIndex + 1),
            ],
          },
          query: ORDER_CONTEXT_QUERY,
          variables: orderContextQueryVariables,
        });
      } catch (e) {
        captureException(e);
      }
    },
  });
};

const translateDraftToValidatedOrder = ({
  draftOrder,
  gtinSettings,
  localScanEntries,
  orderGuide,
  supplierUUID,
  visibleVariantPacks,
}: {
  draftOrder: NewOrder_OrderFieldsFragment;
  gtinSettings?: GtinSettings;
  localScanEntries: Map<string, VariantPackEntry> | null;
  orderGuide: ItemsContext_MultiOrderGuideItemsQuery | null;
  supplierUUID: string;
  visibleVariantPacks: NewOrder_VisibleVariantPackQuery | null;
}): OrderSummary_ValidateOrderMutation => {
  const vpMap = (orderGuide?.getOrderGuideItems || []).reduce(
    (
      acc: {
        [
          vpUUID: string
        ]: ItemsContext_MultiOrderGuideItemsQuery['getOrderGuideItems'][0]['variant_pack'];
      },
      item,
    ) => {
      if (item.variant_pack) {
        acc[item.variant_pack.uuid] = item.variant_pack;
      }
      return acc;
    },
    {},
  );

  const combinedVPMap = (
    visibleVariantPacks?.visible_variant_packs || []
  ).reduce((acc, item) => {
    if (item.variant_pack) {
      acc[item.variant_pack!.uuid] = item.variant_pack;
    }

    return acc;
  }, vpMap);

  return {
    validateOrder: {
      can_place_order: true,
      loyalty: null,
      order: {
        discount_codes: [],
        fulfillment_timeslot_end: draftOrder.fulfillment_timeslot_end,
        fulfillment_timeslot_start: draftOrder.fulfillment_timeslot_start,
        fulfillment_type: draftOrder.fulfillment_type,
        order_promotion_rels: [],
        orders_items: draftOrder.orders_items.map((item) => {
          const ogVP = combinedVPMap[item.variants_pack.uuid];
          const localScanEntry = localScanEntries?.get(item.variants_pack.uuid);

          // If the item has a gtin item name, and the supplier's gtin settings are
          // configured to use the Syndigo name source, use that over the item's display name
          const gtinMetadata = ogVP?.gtin_metadata?.[0];
          const metadata = gtinMetadata?.metadata
            ? (gtinMetadata?.metadata as GTINMetadata)
            : null;
          const gtinItemName = metadata?.item_name;
          const defaultItemName =
            ogVP?.item.display_name ?? item.restaurant_display_name;
          const restaurantDisplayName =
            gtinSettings?.name_source === GtinMetadataFormatType.Syndigo &&
            gtinItemName
              ? gtinItemName
              : defaultItemName;

          const pack = ogVP?.pack ?? {
            base_unit: null,
            max_unit_size: localScanEntry?.pack_max_unit_size ?? 1,
            min_unit_size: localScanEntry?.pack_min_unit_size ?? 1,
            unit: localScanEntry?.pack_unit ?? '',
            unit_count: 1,
            uuid: '',
          };

          return {
            earliest_fulfillment_at: item.earliest_fulfillment_at ?? null,
            order_item_prices: item.order_item_prices,
            order_item_promotion_rels: [],
            orders_items_modifier_options: [],
            pack,
            restaurant_display_name: restaurantDisplayName,
            uuid: item.uuid,
            variants_pack: {
              external_item_id:
                ogVP?.external_item_id ??
                localScanEntry?.external_item_id ??
                null,
              item: item.variants_pack.item,
              metadata: item.variants_pack.metadata,
              uuid: item.variants_pack.uuid,
            },
          };
        }),
        restaurant_desired_delivery_time:
          draftOrder.restaurant_desired_delivery_time,
        status: draftOrder.status,
        supplier: {
          // By excluding supplier_metadata, the lead_time_split_behavior is null and the order
          // summary screen does not try to split the order, which is preferred in offline mode
          // supplier_metadata: { ... },
          uuid: supplierUUID,
        },
        uuid: draftOrder.uuid,
      },
      // Cannot populate order fees offline, instead the order summary
      // will display an estimated total by summing the order items' prices
      order_fees: [],
      section_headers: [],
    },
  };
};

const useOfflineValidateOrder = ({
  supplierUUID,
}: {
  supplierUUID: string;
}): [
  (
    draftOrder: NewOrder_OrderFieldsFragment,
    orderGuide: ItemsContext_MultiOrderGuideItemsQuery | null,
    visibleVariantPacks: NewOrder_VisibleVariantPackQuery | null,
    localScanEntries: Map<string, VariantPackEntry> | null,
  ) => Promise<{ data: OrderSummary_ValidateOrderMutation }>,
  ValidateOrderMutationTuple[1],
] => {
  const [loading, setLoading] = useState(false);
  const { gtinSettings } = useItemDisplayOptions();

  const fetchOfflineValidatedOrder = useCallback(
    async (
      draftOrder: NewOrder_OrderFieldsFragment,
      orderGuide: ItemsContext_MultiOrderGuideItemsQuery | null,
      visibleVariantPacks: NewOrder_VisibleVariantPackQuery | null,
      localScanEntries: Map<string, VariantPackEntry> | null,
    ) => {
      setLoading(true);
      const data = translateDraftToValidatedOrder({
        draftOrder,
        gtinSettings: gtinSettings || undefined,
        localScanEntries,
        orderGuide,
        supplierUUID,
        visibleVariantPacks,
      });
      setLoading(false);
      return { data };
    },
    [gtinSettings, supplierUUID],
  );

  return [fetchOfflineValidatedOrder, { loading }];
};

export const useValidateOrder = ({
  orderDomains,
  restaurantUUID,
  supplierUUID,
}: {
  orderDomains: OrderDomain[];
  restaurantUUID: string;
  supplierUUID: string;
}): ValidateOrderMutationTuple => {
  const [loading, setLoading] = useState(false);
  const [offlineValidateOrder, { loading: offlineLoading }] =
    useOfflineValidateOrder({
      supplierUUID,
    });
  const [onlineValidateOrder, { loading: onlineLoading }] =
    useOnlineValidateOrder({
      orderDomains,
      restaurantUUID,
      supplierUUID,
    });

  const fetchOfflineOrderContext = useCallback(async () => {
    const offlineOrderContextAdapter = OfflineOrderContextAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );
    const offlineOrderContext = await offlineOrderContextAdapter.fetch();
    return offlineOrderContext?.newOrderContext;
  }, [restaurantUUID, supplierUUID]);

  const fetchOfflineOrderGuide = useCallback(async () => {
    const offlineOrderGuideAdapter = new OfflineOrderGuideAdapter(
      restaurantUUID,
      supplierUUID,
    );
    return await offlineOrderGuideAdapter.fetch();
  }, [restaurantUUID, supplierUUID]);

  const fetchOfflineVisibleVariantPacks = useCallback(async () => {
    const offlineVisibleVariantPacksAdapter =
      new OfflineVisibleVariantPackAdapter(restaurantUUID, supplierUUID);
    return await offlineVisibleVariantPacksAdapter.fetch();
  }, [restaurantUUID, supplierUUID]);

  const fetchLocalScanEntries = useCallback(async () => {
    const entries =
      await OfflineMinimalVariantPackAdapter.getInstance(supplierUUID).fetch();
    if (!entries) {
      return null;
    }
    const map = new Map<string, VariantPackEntry>();
    for (const entry of entries) {
      map.set(entry.uuid, entry);
    }
    return map;
  }, [supplierUUID]);

  /**
   * When we call validateOrder (often on focus of the order summary), check if the user
   * is in offline mode. If they are, and have an offline draft order, use it to validate.
   *
   * It's important to check the status of offline mode when the function is called, because
   * it can happen right after the order summary mounts, which will be faster than the
   * useNetworkStatus hook sets its state and re-renders the order summary with the updates.
   *
   * This creates the possibility of the user flickering into offline mode if they briefly
   * lose service. However, that should be handled elsewhere in a way that makes this safe.
   */
  const validateOrder: ValidateOrderMutationTuple[0] = useCallback(
    async (options) => {
      try {
        setLoading(true);
        const isOfflineModeActive = await getOfflineModeActive();
        const orderContext = await fetchOfflineOrderContext();

        const matchingOrder = orderContext?.getOrders.find(
          (draftOrder) =>
            draftOrder.order?.uuid === options?.variables?.orderUUID,
        );

        if (isOfflineModeActive && matchingOrder) {
          const [orderGuide, visibleVariantPacks, localScanEntries] =
            await Promise.all([
              fetchOfflineOrderGuide(),
              fetchOfflineVisibleVariantPacks(),
              fetchLocalScanEntries(),
            ]);

          if (orderGuide || localScanEntries) {
            const offlineOrder = await offlineValidateOrder(
              matchingOrder.order!,
              orderGuide,
              visibleVariantPacks,
              localScanEntries,
            );

            if (offlineOrder) {
              return offlineOrder;
            }
          }
        }

        return onlineValidateOrder(options);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchLocalScanEntries,
      fetchOfflineOrderContext,
      fetchOfflineOrderGuide,
      fetchOfflineVisibleVariantPacks,
      offlineValidateOrder,
      onlineValidateOrder,
    ],
  );

  return [
    validateOrder,
    {
      loading: loading || offlineLoading || onlineLoading,
    },
  ];
};
